import React, { useContext, useEffect, useState } from 'react'
import { apiGet, apiPost } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import Table from './Table'
import Pagination from '../Pagination'
import { useTranslation } from 'react-i18next'
import AuthContext from 'context/AuthContext'
import helpers from '../../utils/helpers'
import OButton from 'components/reusable/OButton'
import { FiDownload } from 'react-icons/fi'
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker'
import dayjs from 'dayjs'
import OSearch from 'components/reusable/OSearch'
import CustomSelect from 'components/reusable/CustomSelect'
import { statusOptions } from 'utils/constants'
import { startCase } from 'lodash'
import * as XLSX from 'xlsx'

function RegisteredCompanies () {
  const { t } = useTranslation()
  const [paginationObj, setPaginationObj] = useState({
    page: 1,
    pageCount: 1,
    pageRangeDisplayed: 10
  })
  const { logoutUser, updatePageName, user } = useContext(AuthContext)
  const manager = user?.permission?.find(e => e.manager === 'reports') ?? {}
  const [registeredCompanies, setRegisteredCompanies] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)
  const [isDelete, setIsDelete] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [sort, setSort] = useState({
    sortBy: 'createdAt',
    sortType: 'desc'
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [filterData, setFilterData] = useState({
    category: '',
    searchKey: '',
    startDate: '',
    endDate: '',
    isReset: false,
    isFilter: false
  })

  const getAllCompany = async payload => {
    try {
      const { category, searchKey, startDate, endDate } = filterData
      if (payload?.deletePage) {
        payload = {
          page,
          pageSize,
          status: category,
          sortBy: sort.sortBy,
          sortType: sort.sortType,
          type: 'company',
          keyword: searchKey,
          startDate: helpers?.ternaryCondition(
            startDate,
            dayjs(startDate).format('YYYY-MM-DD'),
            null
          ),
          endDate: helpers?.ternaryCondition(
            endDate,
            dayjs(endDate).format('YYYY-MM-DD'),
            null
          )
        }
        if (registeredCompanies?.length >= 1) {
          setPage(page - 1)
          setIsDelete(true)
          setPaginationObj({ ...paginationObj, page: page - 1 })
        }
      } else {
        payload = {
          page,
          pageSize,
          status: category,
          sortBy: sort.sortBy,
          sortType: sort.sortType,
          type: 'company',
          keyword: searchKey?.trim(),
          startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
          endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null
        }
        setIsDelete(false)
      }
      const path = apiPath.getRegisteredCompany
      const result = await apiGet(path, payload)
      if (result?.status === 200) {
        const response = result?.data?.results
        const resultStatus = result?.data?.success
        setRegisteredCompanies(response?.docs)
        setPaginationObj({
          ...paginationObj,
          pageCount: helpers?.ternaryCondition(
            resultStatus,
            response.totalPages,
            null
          ),
          perPageItem: helpers?.ternaryCondition(
            resultStatus,
            response?.docs.length,
            null
          ),
          totalItems: helpers?.ternaryCondition(
            resultStatus,
            response.totalDocs,
            null
          )
        })
      }
    } catch (error) {
      console.log(
        'error in get all registered company list==>>>>',
        error.message
      )
      if (error.response.status === 401 || error.response.status === 409) {
        logoutUser()
      }
    }
  }

  const handlePageClick = event => {
    const newPage = event.selected + 1
    setPage(newPage)
  }

  const statusPage = e => {
    setFilterData({
      ...filterData,
      isReset: false,
      category: e.target.value,
      isFilter: true
    })
    setPage(1)
  }

  useEffect(() => {
    getAllCompany()
  }, [page, sort, filterData])

  const handleDateChange = (start, end) => {
    setPage(1)
    setFilterData({
      ...filterData,
      startDate: start,
      endDate: end,
      isFilter: true
    })
  }

  useEffect(() => {
    helpers.setDebouncingFunction({
      isInitialized,
      setIsInitialized,
      debouncedSearchTerm,
      searchTerm,
      filterData,
      setFilterData,
      setPage
    })
  }, [debouncedSearchTerm])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [searchTerm])

  const handleReset = () => {
    setFilterData({
      category: '',
      searchKey: '',
      isReset: true,
      isFilter: false,
      setStatus: '',
      startDate: '',
      endDate: ''
    })
    setPage(1)
    setSearchTerm('')
  }

  useEffect(() => {
    updatePageName(t('REGISTERED_COMPANIES'))
  }, [])

  const handleDownloadReport = async () => {
    if (isExporting) return
    try {
      setIsExporting(true)
      const { category, searchKey, startDate, endDate } = filterData

      const sendData = {
        type: 'company',
        status: category || null,
        keyword: searchKey?.trim() || null,
        sortBy: sort.sortBy,
        sortType: sort.sortType,
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : null,
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : null
      }

      const res = await apiPost(apiPath.downloadRegisteredCompany, sendData)
      const rows = res?.data?.results?.docs

      if (!res?.data?.success || !rows || rows.length === 0) {
        alert(t('O_NO_RECORD_FOUND') || 'No data available to export')
        return
      }

      const dataToExport = rows.map((item, index) => ({
        'Sr No': index + 1,
        ID: item?._id,
        'Company Name': item?.name || 'N/A',
        'Email ID': item?.officialEmail,
        'Company Number': item?.companyNumber,
        Country: item?.country,
        City: item?.city,
        'Created Date': item?.createdAt,
        Status: startCase(item?.status)
      }))

      const worksheet = XLSX.utils.json_to_sheet(dataToExport)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Registered Companies')

      const fileName = `Registered_Companies_${dayjs().format('YYYY-MM-DD')}.xlsx`
      XLSX.writeFile(workbook, fileName)
    } catch (error) {
      console.log('error in download report:', error)
      if (error?.response?.status === 401 || error?.response?.status === 409) {
        logoutUser()
      }
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div>
      <div className='bg-[#F9F9F9]'>
        <div className='px-3 py-4'>
          <div className='border-[#E9EDF9] bg-white border rounded-lg'>
            <form
              onSubmit={e => e.preventDefault()}
              className='2xl:flex gap-2 border-b border-b-[#E3E3E3] px-4 py-3'
            >
              <div className='flex-wrap  items-center mb-2 col-span-2 flex 2xl:mb-0'>
                <div className='flex lg:pt-0 pt-3 flex-wrap items-center '>
                  <div className='relative items-center mb-3 flex '>
                    <OSearch
                      placeholder={t('KEYWORD')}
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  </div>
                  <ODateRangePicker
                    handleDateChange={handleDateChange}
                    setIsReset={setFilterData}
                    isReset={filterData?.isReset}
                  />
                  <CustomSelect
                    onChange={statusPage}
                    options={statusOptions}
                    placeholder='O_ALL'
                    id='category'
                    name='category'
                    value={filterData.category}
                    t={t}
                  />

                  <OButton
                    label='Reset'
                    extraClasses='mb-3 ml-3'
                    type='button'
                    onClick={handleReset}
                    style={{ borderRadius: '0.5rem' }}
                  />
                </div>
              </div>
              <div className='justify-end px-4 ms-auto flex items-center  mb-3'>
                <button
                  type='button'
                  onClick={handleDownloadReport}
                  disabled={isExporting}
                  title={t('EXPORT')}
                  className='bg-LightBlue flex text-sm px-8 ml-3 py-2 rounded-lg items-center border border-transparent text-white hover:bg-DarkBlue whitespace-nowrap disabled:opacity-50'
                >
                  <FiDownload className='mr-3' />
                  {isExporting ? 'Exporting...' : t('EXPORT')}
                </button>
              </div>
            </form>
            <Table
              registeredCompanies={registeredCompanies}
              getAllCompany={getAllCompany}
              page={page}
              setSort={setSort}
              sort={sort}
              manager={manager}
            />
            {paginationObj?.totalItems ? (
              <Pagination
                handlePageClick={handlePageClick}
                options={paginationObj}
                isDelete={isDelete}
                page={page}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RegisteredCompanies